import React, { useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { AsyncDataTable, DataTableColumn, TableActions } from '@saucelabs/ds-table';
import { records as appStorageRecords, constants as appStorageConstants } from '@saucelabs/sl-module-app-storage';
import { IconDependencyOutline } from '@saucelabs/ds-icons';
import { ModalContext } from '@saucelabs/ds-modal';

import { MANUAL_TEST_EVENTS, trackEvent } from '../../../tracking';
import * as globalActions from '../../../actions';
import constants from '../constants';
import selectors from '../selectors';
import actions from '../actions';

type RowData = {
  build: string;
  description: string;
  file: appStorageRecords.AppStorageFile;
  id: string;
  isActive: boolean;
  name: string;
  uploaded: Date;
  version: string;
};

const FilesList: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const {
    currentDevice,
    currentFile,
    files,
  } = useSelector(selectors.filesModalSelector);
  const {
    currentPage,
    totalItems,
  } = useSelector(selectors.filesListPaginationSelector);
  const { hideModal } = useContext(ModalContext); // eslint-disable-line @typescript-eslint/unbound-method

  const handlePagination = useCallback((newPage: number) => {
    void dispatch(actions.fetchAppStorageFiles({
      paginationPage: newPage,
    }));
  }, [dispatch]);

  const switchAppVersion = useCallback((file: appStorageRecords.AppStorageFile) => {
    void dispatch(globalActions.installMobileApp({
      appId: file.id,
      disconnectIo: true,
      groupId: currentFile.group_id,
    }));

    // update <SessionInfoModal />
    // TODO: LT-2600
    void dispatch(globalActions.fetchMobileAppInfo({ appId: file.id }));

    hideModal?.();

    trackEvent(MANUAL_TEST_EVENTS.SELECT_APP_VERSION, {
      deviceName: currentDevice?.name,
      os: currentDevice?.os,
    });
  }, [currentDevice, currentFile, hideModal, dispatch]);

  const columns: DataTableColumn<RowData>[] = useMemo(() => (
    [
      {
        allowsSorting: false,
        customBodyRender: ((value, key, row) => (
          <div className={classnames('break-all', { 'text-disabled-emphasis': row.isActive })}>{value as string}</div>
        )),
        field: 'name',
        key: 'name',
        name: 'File Name',
      },
      {
        allowsSorting: false,
        customBodyRender: ((value, key, row) => (
          <div className={classnames('break-all', { 'text-disabled-emphasis': row.isActive })}>{value as string}</div>
        )),
        field: 'id',
        key: 'id',
        name: 'File ID',
      },
      {
        allowsSorting: false,
        customBodyRender: ((value, key, row) => (
          <div className={classnames('break-all', { 'text-disabled-emphasis': row.isActive })}>{value as string}</div>
        )),
        field: 'description',
        hiddenBreakpoints: ['xs', 'sm', 'md'],
        key: 'description',
        name: 'Description',
        widthClass: 'w-24',
      },
      {
        allowsSorting: false,
        customBodyRender: ((value, key, row) => (
          <div className={classnames('break-all', { 'text-disabled-emphasis': row.isActive })}>{value as string}</div>
        )),
        field: 'version',
        helpText: (() => {
          if (currentFile.kind) {
            if (currentFile.kind === appStorageConstants.APP_STORAGE_KIND.IOS) {
              return 'The value of "CFBundleShortVersionString" in Info.plist';
            }
            if (currentFile.kind === appStorageConstants.APP_STORAGE_KIND.ANDROID) {
              return 'The value of "versionName" in the app manifest';
            }
          }
          return '';
        })(),
        key: 'version',
        name: 'Version',
      },
      {
        allowsSorting: false,
        customBodyRender: ((value, key, row) => (
          <div className={classnames('break-all', { 'text-disabled-emphasis': row.isActive })}>{value as string}</div>
        )),
        name: 'Build',
        field: 'build',
        key: 'build',
        helpText: (() => {
          if (currentFile.kind) {
            if (currentFile.kind === appStorageConstants.APP_STORAGE_KIND.IOS) {
              return 'The value of "CFBundleVersion" in Info.plist';
            }
            if (currentFile.kind === appStorageConstants.APP_STORAGE_KIND.ANDROID) {
              return 'The value of "versionCode" in the app manifest';
            }
          }
          return '';
        })(),
      },
      {
        allowsSorting: true,
        customBodyRender: ((value, key, row) => (
          <div className={classnames('break-all', { 'text-disabled-emphasis': row.isActive })}>
            {row.file.getCreationDatePrettyLabel()}
          </div>
        )),
        field: 'uploaded',
        helpText: `Uploaded files will expire after ${appStorageConstants.APP_STORAGE_LIFETIME_DAYS} days`,
        key: 'uploaded',
        name: 'Uploaded',
      },
      {
        allowsSorting: false,
        customBodyRender: (value, key, row) => {
          if (row.isActive) {
            return <div className="text-right text-green-400">ACTIVE</div>;
          }
          return (
            <div className="w-9">
              <TableActions actions={[
                {
                  label: 'Select',
                  onPress: () => switchAppVersion(row.file),
                  icon: IconDependencyOutline,
                  primaryAction: true,
                },
              ]}
              />
            </div>
          );
        },
        field: 'id',
        key: 'actions',
        name: '',
      },
    ]
  ), [currentFile, switchAppVersion]);

  const rows = useMemo(
    () => (
      files.map((file) => ({
        build: file?.getBuild(),
        description: file?.description,
        file,
        id: file?.id,
        isActive: file.id === currentFile.id,
        name: file?.name,
        uploaded: file?.getCreationDate(),
        version: file?.getVersion(),
      }))),
    [files, currentFile],
  );

  return (
    <AsyncDataTable
      aria-label="Switch App Versions Files List"
      columns={columns}
      isPaginated
      itemsPerPage={constants.FETCH_FILES_PAGE_SIZE}
      itemsPerPageOptions={undefined}
      onPageNumberChange={handlePagination}
      pageNumber={currentPage}
      rows={rows}
      totalItemCount={totalItems}
    />
  );
};

FilesList.displayName = 'SwitchAppVersionFilesList';

export default FilesList;
